/** @jsxImportSource @emotion/react */
import PropTypes from 'prop-types'
import {
  cardStyle,
  cardStyleSelected,
  cardThumbnailStyle,
  cardContentStyle,
  cardThumbnailEmptyStyle,
  cardAssistsStyle,
  cardTimeStyle,
  cardTypeStyle,
  homeTeamLabelStyle,
  visitorTeamLabelStyle,
} from '../styles'

/**
 * HighlightCard component for displaying individual highlight items
 * @param {Object} props - Component props
 * @param {Object} props.highlight - Highlight data
 * @param {Function} props.onClick - Click handler
 * @param {boolean} props.isPlaying - Whether this card's video is currently playing
 * @returns {JSX.Element} HighlightCard component
 */
const HighlightCard = ({ highlight, onClick, isPlaying }) => {
  console.log(highlight)
  const handleClick = () => {
    if (onClick) {
      onClick(highlight)
    }
  }

  return (
    <div css={isPlaying ? cardStyleSelected : cardStyle} onClick={handleClick}>
      <img
        src={
          highlight.thumbnail_url ||
          highlight.thumb ||
          '/images/generic_placeholders/placeholder_video.png'
        }
        alt={`${highlight.title || highlight.type} highlight`}
        css={
          highlight.thumbnail_url || highlight.thumb
            ? cardThumbnailStyle
            : cardThumbnailEmptyStyle
        }
        onError={(e) => {
          e.target.src = '/images/generic_placeholders/placeholder_video.png'
        }}
      />
      <div css={cardContentStyle}>
        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {/* Left side: Title and body/assists */}
          <div
            css={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              flex: 1,
              minWidth: 0,
            }}
          >
            <div css={cardTypeStyle}>{highlight.title || highlight.type}</div>
            {highlight.assists && highlight.assists.length > 0 ? (
              <div>
                {highlight.assists.map((assist, index) => (
                  <div key={assist.id || index} css={cardAssistsStyle}>
                    Assisted by: {assist.player?.name_full || assist}
                  </div>
                ))}
              </div>
            ) : (
              <div css={cardAssistsStyle}>{highlight.body}</div>
            )}
          </div>

          {/* Right side: Time */}
          <div css={cardTimeStyle}>
            {highlight.period_clock_time
              ? highlight.period_clock_time.split(':').slice(-2).join(':')
              : ''}
          </div>
        </div>
      </div>
      {highlight.teamSide && (
        <div
          css={
            highlight.teamSide === 'Home'
              ? homeTeamLabelStyle
              : visitorTeamLabelStyle
          }
        >
          {highlight.teamSide.toUpperCase()}
        </div>
      )}
    </div>
  )
}

HighlightCard.propTypes = {
  highlight: PropTypes.shape({
    id: PropTypes.string.isRequired,
    // API fields
    game_id: PropTypes.string,
    player_id: PropTypes.string,
    team_id: PropTypes.string,
    title: PropTypes.string,
    body: PropTypes.string,
    video_url: PropTypes.string,
    thumbnail_url: PropTypes.string,
    video_duration: PropTypes.string,
    period_clock_time: PropTypes.string,
    // Assists field
    assists: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          id: PropTypes.string,
          player: PropTypes.shape({
            name_full: PropTypes.string,
          }),
        }),
      ])
    ),
    // Enriched fields
    teamSide: PropTypes.oneOf(['Home', 'Visitor']),
    playerName: PropTypes.string,
    teamName: PropTypes.string,
    // Legacy mock fields (for backward compatibility)
    type: PropTypes.string,
    time: PropTypes.string,
    player: PropTypes.string,
    thumb: PropTypes.string,
    videoUrl: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func,
  isPlaying: PropTypes.bool,
}

HighlightCard.defaultProps = {
  onClick: null,
  isPlaying: false,
}

export default HighlightCard
